import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SbRow, SbCol, SbSpin } from 'uiCore';
import { FileTextOutlined, QuestionCircleOutlined, TeamOutlined, PictureOutlined } from '@ant-design/icons';
import AdminPageHeader from '../components/AdminPageHeader';
import SetupCard from '../components/SetupCard';
import { getStatusBadge } from '../components/cardHelpers';
import AboutCompanyModal from '../components/modals/AboutCompanyModal';
import ValuesCultureModal from '../components/modals/ValuesCultureModal';
import BannerModal from '../components/modals/BannerModal';
import useOrgContext from '../hooks/useOrgContext';
import { PageWrapperStyled, PageLoaderStyled, HeaderDividerStyled } from '../AdminSetup.styles';

/**
 * JdManagement — page reached from the "JD Management" card in Module Settings.
 * Shows three options:
 *   - Manage JD Template → navigates to the JD Templates page (child routing)
 *   - About Company      → opens the About Company modal
 *   - Values & Culture   → opens the Values & Culture modal
 * (JD Banner is parked for the future.)
 */
const JdManagement = () => {
  const navigate = useNavigate();
  const { data, loading, updateOrgContext } = useOrgContext();
  const [modalVisible, setModalVisible] = useState({
    about_company: false,
    values_culture: false,
    banner: false,
  });

  const cards = data?.cards || {};

  const openModal = (key) => setModalVisible((prev) => ({ ...prev, [key]: true }));
  const closeModal = (key) => setModalVisible((prev) => ({ ...prev, [key]: false }));

  return (
    <PageWrapperStyled>
      <AdminPageHeader title="JD Management" />

      <HeaderDividerStyled />

      {loading ? (
        <PageLoaderStyled align="center" justify="center">
          <SbSpin size="large" />
        </PageLoaderStyled>
      ) : (
        <SbRow gutter={[14, 14]}>
          <SbCol xs={24} sm={12} md={8} lg={24 / 5} xl={4}>
            <SetupCard
              icon={<FileTextOutlined />}
              title="Manage JD Template"
              description="Base templates that scaffold every AI-generated JD"
              onClick={() => navigate('/admin-setup/jd-templates')}
            />
          </SbCol>

          <SbCol xs={24} sm={12} md={8} lg={24 / 5} xl={4}>
            <SetupCard
              icon={<QuestionCircleOutlined />}
              title="About Company"
              description="Public company overview appended to published JDs"
              badge={getStatusBadge(cards.about_company?.status || 'empty')}
              onClick={() => openModal('about_company')}
            />
          </SbCol>

          <SbCol xs={24} sm={12} md={8} lg={24 / 5} xl={4}>
            <SetupCard
              icon={<TeamOutlined />}
              title="Values & Culture"
              description="Core values & culture used in role profiles and PAIC"
              badge={getStatusBadge(cards.values_culture?.status || 'empty')}
              onClick={() => openModal('values_culture')}
            />
          </SbCol>

          <SbCol xs={24} sm={12} md={8} lg={24 / 5} xl={4}>
            <SetupCard
              icon={<PictureOutlined />}
              title="JD Banner"
              description="Banner image shown at the top of every generated JD"
              onClick={() => openModal('banner')}
            />
          </SbCol>
        </SbRow>
      )}

      <AboutCompanyModal
        visible={modalVisible.about_company}
        onClose={() => closeModal('about_company')}
        initialData={cards.about_company?.data || {}}
        cardKey="about_company"
        onSave={updateOrgContext}
      />

      <ValuesCultureModal
        visible={modalVisible.values_culture}
        onClose={() => closeModal('values_culture')}
        initialData={cards.values_culture?.data || {}}
        cardKey="values_culture"
        onSave={updateOrgContext}
      />

      <BannerModal
        visible={modalVisible.banner}
        onClose={() => closeModal('banner')}
      />
    </PageWrapperStyled>
  );
};

export default JdManagement;
